import { useState, useRef, useEffect, useMemo } from 'react'
import { Search } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { useAllActivePriorities } from '@/hooks/usePriorities'
import { useJournalEntries, useCreateJournalEntry } from '@/hooks/useJournal'
import { useBuckets } from '@/hooks/useBuckets'
import { cn } from '@/lib/utils'
import type { AppMode } from '@/components/layout/AppShell'
import type { JournalEntry } from '@/types'

// ─── Types ────────────────────────────────────────────────────────────────────

interface PaletteItem {
  id: string
  group: 'navigate' | 'priority' | 'entry'
  label: string
  sublabel?: string
  color?: string
  onSelect: () => void | Promise<void>
}

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onNavigate: (mode: AppMode) => void
}

// ─── Public events ────────────────────────────────────────────────────────────

export function dispatchOpenPriority(id: string) {
  window.dispatchEvent(new CustomEvent('plumb:open-priority', { detail: { id } }))
}

export function dispatchOpenEntry(id: string) {
  window.dispatchEvent(new CustomEvent('plumb:open-entry', { detail: { id } }))
}

// ─── Shell ────────────────────────────────────────────────────────────────────

export function CommandPalette({ open, onOpenChange, onNavigate }: CommandPaletteProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 max-w-lg overflow-hidden gap-0 [&>button]:hidden">
        {open && (
          <PaletteContent
            onNavigate={onNavigate}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

// ─── Content (mounted only when open — hooks are lazy) ────────────────────────

const GROUP_LABEL: Record<PaletteItem['group'], string> = {
  navigate: 'Actions',
  priority: 'Priorities',
  entry:    'Journal entries',
}

const GROUP_ORDER: PaletteItem['group'][] = ['navigate', 'priority', 'entry']

function PaletteContent({
  onNavigate,
  onClose,
}: {
  onNavigate: (mode: AppMode) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const priorities = useAllActivePriorities()
  const buckets    = useBuckets()
  const entries    = useJournalEntries()
  const createEntry = useCreateJournalEntry()

  const bucketMap = useMemo(
    () => new Map((buckets ?? []).map((b) => [b.id, b])),
    [buckets],
  )

  const q = query.trim().toLowerCase()

  // ── Item builder ──────────────────────────────────────────────────────────

  const items = useMemo<PaletteItem[]>(() => {
    const result: PaletteItem[] = []

    // Navigate / action items
    const navItems: PaletteItem[] = [
      { id: 'nav-detail',   group: 'navigate', label: 'Go to Detail view',   sublabel: 'D', onSelect: () => { onNavigate('detail');   onClose() } },
      { id: 'nav-journal',  group: 'navigate', label: 'Go to Journal view',  sublabel: 'J', onSelect: () => { onNavigate('journal');  onClose() } },
      { id: 'nav-calendar', group: 'navigate', label: 'Go to Calendar view', sublabel: 'C', onSelect: () => { onNavigate('calendar'); onClose() } },
      {
        id: 'new-free',
        group: 'navigate',
        label: 'New free entry',
        onSelect: async () => {
          const id = await createEntry.mutateAsync({ type: 'free' })
          onNavigate('journal')
          dispatchOpenEntry(id)
          onClose()
        },
      },
      {
        id: 'new-discernment',
        group: 'navigate',
        label: 'New discernment entry',
        onSelect: async () => {
          const id = await createEntry.mutateAsync({ type: 'discernment' })
          onNavigate('journal')
          dispatchOpenEntry(id)
          onClose()
        },
      },
      {
        id: 'new-review',
        group: 'navigate',
        label: 'New weekly review',
        onSelect: async () => {
          const id = await createEntry.mutateAsync({ type: 'weekly_review' })
          onNavigate('journal')
          dispatchOpenEntry(id)
          onClose()
        },
      },
    ]

    result.push(...(q ? navItems.filter((a) => a.label.toLowerCase().includes(q)) : navItems))

    // Priorities — only when there is a query
    if (q && priorities) {
      const matched = priorities.filter((p) => p.title.toLowerCase().includes(q)).slice(0, 8)
      for (const p of matched) {
        const bucket = bucketMap.get(p.bucketId ?? '')
        result.push({
          id: `priority-${p.id}`,
          group: 'priority',
          label: p.title,
          sublabel: bucket?.name,
          color: bucket?.color,
          onSelect: () => {
            onNavigate('detail')
            dispatchOpenPriority(p.id)
            onClose()
          },
        })
      }
    }

    // Journal entries — only when there is a query
    if (q && entries) {
      const matched = entries
        .filter((e) => e.body.toLowerCase().includes(q) || (e as JournalEntry & { entryDate: string }).entryDate.includes(q))
        .slice(0, 4)
      for (const e of matched) {
        const preview = e.body.replace(/[#*`_>\-[\]]/g, '').trim().slice(0, 60) || 'Empty entry'
        result.push({
          id: `entry-${e.id}`,
          group: 'entry',
          label: preview,
          sublabel: e.entryDate,
          onSelect: () => {
            onNavigate('journal')
            dispatchOpenEntry(e.id)
            onClose()
          },
        })
      }
    }

    return result
  }, [q, priorities, entries, bucketMap, onNavigate, onClose, createEntry])

  // Reset highlight when items change
  useEffect(() => setHighlighted(0), [items])

  // Scroll highlighted item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${highlighted}"]`) as HTMLElement | null
    el?.scrollIntoView({ block: 'nearest' })
  }, [highlighted])

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, items.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)) }
    if (e.key === 'Enter')     { e.preventDefault(); void items[highlighted]?.onSelect() }
  }

  // Group items for sectioned display
  const grouped = useMemo(() => {
    let flatIdx = 0
    return GROUP_ORDER.flatMap((g) => {
      const gItems = items
        .filter((i) => i.group === g)
        .map((i) => ({ ...i, flatIdx: flatIdx++ }))
      return gItems.length > 0 ? [{ group: g, items: gItems }] : []
    })
  }, [items])

  return (
    <>
      {/* Search row */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
        <Search size={15} className="text-muted-foreground flex-shrink-0" />
        <input
          ref={inputRef}
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Jump to, search, or create…"
          className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"
          aria-label="Command palette"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded
        />
        <kbd className="text-[10px] text-muted-foreground border border-border rounded px-1 py-0.5 font-mono">
          esc
        </kbd>
      </div>

      {/* Results */}
      <div ref={listRef} className="max-h-80 overflow-y-auto py-1.5" role="listbox">
        {items.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-10 italic">
            No results for "{query}"
          </p>
        )}

        {grouped.map(({ group, items: gItems }) => (
          <div key={group}>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-4 pt-3 pb-1">
              {GROUP_LABEL[group]}
            </p>
            {gItems.map((item) => (
              <button
                key={item.id}
                type="button"
                data-idx={item.flatIdx}
                onClick={() => void item.onSelect()}
                onMouseEnter={() => setHighlighted(item.flatIdx)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-4 py-2 text-sm text-left transition-colors',
                  item.flatIdx === highlighted
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent/50 text-foreground',
                )}
                role="option"
                aria-selected={item.flatIdx === highlighted}
              >
                {item.color && (
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                    aria-hidden
                  />
                )}
                <span className="flex-1 truncate">{item.label}</span>
                {item.sublabel && (
                  <span className="text-xs text-muted-foreground flex-shrink-0">{item.sublabel}</span>
                )}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="border-t border-border px-4 py-2 flex items-center gap-4 text-[10px] text-muted-foreground">
        <span><kbd className="font-mono">↑↓</kbd> navigate</span>
        <span><kbd className="font-mono">↵</kbd> select</span>
        <span><kbd className="font-mono">esc</kbd> close</span>
      </div>
    </>
  )
}
